import { Component, Inject, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { FileService } from 'src/app/services/api/file.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { formatMoneyDisplay, moneyPair } from 'src/shared/lib/money';

@Component({
  selector: 'app-editar-number-tickets',
  templateUrl: './editar-number-tickets.component.html',
  styleUrls: ['./editar-number-tickets.component.css']
})
export class EditarNumberTicketsComponent implements OnInit {
  loading = false;
  success = false;
  usuario: string | null;

  totalGeneral = 0;
  /** Símbolo de UI: `$` o `S/` (nunca D/S crudos). */
  moneda = 'S/';
  resumen: string | null = null;
  /** Flag para habilitar Guardar (CD confiable vs método en template). */
  hasChanges = false;
  dataSource = new MatTableDataSource<any>([]);

  ticketsChanges: any = {};
  servicesChanges: any = {};

  constructor(
    public dialogRef: MatDialogRef<EditarNumberTicketsComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private fileService: FileService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private notificationService: NotificationService
  ) {
    this.moneda = this.normalizeMoneda(
      this.data?.moneda ?? this.data?.tipoMoneda
    );
    this.usuario = null;
    this.dataSource = new MatTableDataSource(
      this.handleFormatearTarifasTickets(
        this.data.paxTickets ?? {},
        this.data.tarifaTicket ?? {}
      )
    );
  }

  /** Misma regla que Sales EditTicketCantidadDialog / table-expand.normalizeMoneda. */
  normalizeMoneda(raw: string | null | undefined): string {
    if (!raw) return 'S/';
    const value = String(raw).toUpperCase().trim();
    if (
      value === 'D' ||
      value === '$' ||
      value === 'USD' ||
      value.includes('DOLAR')
    ) {
      return '$';
    }
    return 'S/';
  }

  formatMoney(amount: number | string | null | undefined): string {
    if (amount === '' || amount == null || amount === undefined) return '-';
    const n = Number(amount);
    if (!Number.isFinite(n)) return '-';
    return `${this.moneda} ${formatMoneyDisplay(n)}`;
  }

  handleFormatearTarifasTickets(
    paxTickets: Record<string, number>,
    tarifaTicket: Record<string, number>
  ) {
    const keys = [
      'ticketAdulto',
      'ticketEstudiante',
      'ticketNacional',
      'ticketNino'
    ];
    const listFormatedTarifs = [];
    for (const key of keys) {
      const unit = Number(tarifaTicket?.[key]);
      const qty = Number(paxTickets?.[key] ?? 0);
      const hasUnit = Number.isFinite(unit);
      listFormatedTarifs.push({
        key,
        tipo: this.handleTipoTicket(key),
        monto: hasUnit ? unit : '',
        cantidad: Number.isFinite(qty) ? qty : 0,
        min: 0,
        total: hasUnit && qty ? unit * qty : ''
      });
    }

    return listFormatedTarifs;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ticketsQuantity'] || changes['tarifa']) {
      this.calcularTotales();
    }
  }

  handleChangeQuantity(element: any, valueOrEvent: number | Event) {
    let value: number;
    if (element.monto === '') return;
    if (typeof valueOrEvent === 'number') {
      value = valueOrEvent;
    } else {
      const input = valueOrEvent.target as HTMLInputElement;
      value = input.valueAsNumber;
    }

    if (isNaN(value)) {
      value = 0;
    }

    if (value < element.min) {
      value = element.min;
    }

    element.cantidad = value;
    element.total = value * element.monto;
    this.calcularTotales();
    this.refreshHasChanges();
  }

  calcularTotales() {
    if (!this.dataSource.data || this.dataSource.data.length === 0) {
      this.totalGeneral = 0;
      this.resumen = null;
      return;
    }

    let total = 0;
    const resumenLines: string[] = [];

    for (const element of this.dataSource.data) {
      const cantidad = element.cantidad ?? 0;
      const monto = element.monto ?? 0;

      if (cantidad > 0) {
        if (monto) {
          total += monto * cantidad;
        }
        // Misma redacción que Sales EditTicketCantidadDialog
        resumenLines.push(`${cantidad} - ${element.tipo}`);
      }
    }

    this.totalGeneral = total;
    this.resumen = resumenLines.length ? resumenLines.join('<br/>') : null;
  }

  private refreshHasChanges(): void {
    const original = this.data?.paxTickets ?? {};
    const keys = [
      'ticketAdulto',
      'ticketEstudiante',
      'ticketNacional',
      'ticketNino'
    ];
    this.hasChanges = keys.some((key) => {
      const row = this.dataSource.data.find((d: any) => d.key === key);
      return Number(original[key] ?? 0) !== Number(row?.cantidad ?? 0);
    });
  }

  isthereChanges() {
    return this.hasChanges;
  }

  handleTipoTicket(tipoTicket: string) {
    const equivalencia: Record<string, string> = {
      ticketAdulto: 'Ticket Adulto',
      ticketNacional: 'Ticket Nacional',
      ticketEstudiante: 'Ticket Estudiante',
      ticketNino: 'Ticket Niño'
    };
    return equivalencia[tipoTicket];
  }

  handleGetPaxTicket(lista: any[]) {
    const paxticket: Record<string, number> = {
      ticketAdulto: 0,
      ticketNacional: 0,
      ticketEstudiante: 0,
      ticketNino: 0
    };

    for (const element of lista) {
      paxticket[element.key] = element.cantidad;
    }
    this.ticketsChanges.paxTickets = paxticket;
    return this.ticketsChanges.paxTickets;
  }

  getNewCostos() {
    const tipoCambio = Number(this.data.tipoCambio);
    if (!Number.isFinite(tipoCambio) || tipoCambio <= 0) {
      throw new Error('Tipo de cambio no disponible en el servicio');
    }

    const total = Number(Number(this.totalGeneral).toFixed(2));
    const pair = moneyPair(total, this.moneda === 'S/' ? 'S' : 'D', tipoCambio);
    this.ticketsChanges.newTotalCostoSoles = pair.soles;
    this.ticketsChanges.newTotalCostoDolares = pair.dollars;

    return {
      newTotalCostoDolares: this.ticketsChanges.newTotalCostoDolares,
      newTotalCostoSoles: this.ticketsChanges.newTotalCostoSoles
    };
  }

  getMontoServicio() {
    const montosTickets: number[] =
      this.data.tickets.map((item: any) => {
        if (item.id === this.data.id) return this.totalGeneral;
        return item.monto;
      }) ?? [];
    return montosTickets
      .filter((n) => typeof n === 'number')
      .reduce((acc, valor) => acc + valor, 0);
  }

  onSave() {
    if (this.loading || !this.isthereChanges())
      return;

    const tipoCambio = Number(this.data.tipoCambio);
    if (!Number.isFinite(tipoCambio) || tipoCambio <= 0) {
      this.notificationService.showErrorssAlertPersonalizado(
        'Tipo de cambio no disponible',
        'No se encontró un tipo de cambio válido en el servicio. No se puede convertir el monto.',
        () => undefined
      );
      return;
    }

    this.loading = true;
    let ticketChanges: any;
    try {
      ticketChanges = {
        id: this.data.id,
        paxTickets: this.handleGetPaxTicket(this.dataSource.data),
        editOperation: true,
        ...this.getNewCostos()
      };
    } catch (err: any) {
      this.loading = false;
      this.notificationService.showErrorssAlertPersonalizado(
        'Tipo de cambio no disponible',
        err?.message || 'No se pudo convertir el monto.',
        () => undefined
      );
      return;
    }

    const montoServicio = this.getMontoServicio();

    this.fileService
      .updatePaxTicket(
        this.data.servicioId,
        ticketChanges,
        montoServicio,
        this.usuario ?? ''
      )
      .pipe(
        tap((resp) => {
          this.success = true;
          this.servicesChanges = resp.updatePaxTicket;
          this.servicesChanges.editOperation = true;
          // Cerrar primero: el aviso de éxito no debe bloquear el cierre.
          this.OnClose();
          this.notificationService.showSuccessAlertPersonalizado(
            'Tickets Editados con Éxito',
            'Cantidad de tickets fue actualizado correctamente.'
          );
        }),
        catchError((err) => {
          this.notificationService.notifyError(err.message);
          return throwError(() => err);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.usuario = localStorage.getItem('email');
    this.calcularTotales();
    this.refreshHasChanges();
  }

  OnClose() {
    this.dialogRef.close({
      success: this.success,
      servicesChanges: this.servicesChanges,
      ticketsChanges: this.ticketsChanges
    });
  }

  onCancel() {
    this.OnClose();
  }

  ngOnDestroy(): void {
    this.OnClose();
  }
}
